import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Facebook, Twitter, Instagram } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [footerContent, setFooterContent] = useState(null);

  useEffect(() => {
    const fetchFooterContent = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/site-content/footer`);
        setFooterContent(res.data);
      } catch (err) {
        // Use default content on error
        console.log('Using default footer content');
      }
    };
    fetchFooterContent();
  }, []);

  const renderLinks = (section) => {
    if (!section?.links) return null;
    const directRoutes = ['/host', '/how-it-works', '/privacy', '/help', '/about'];
    
    return section.links.map((link, index) => {
      if (directRoutes.includes(link.url) || link.direct) {
        return (
          <li key={index}>
            <Link 
              to={link.url} 
              className="text-gray-300 hover:text-white hover:underline text-sm"
            >
              {link.label}
            </Link>
          </li>
        );
      }
      
      const slug = link.url?.replace(/^\//, '') || '';
      return (
        <li key={index}>
          <Link 
            to={`/page/${slug}`}
            className="text-gray-300 hover:text-white hover:underline text-sm"
          >
            {link.label}
          </Link>
        </li>
      );
    });
  };

  // Default content while loading
  const defaultSupport = {
    links: [
      { label: 'Help Center', url: '/help' },
      { label: 'Safety information', url: '/safety' },
      { label: 'Dietary accommodations', url: '/dietary' },
      { label: 'Cancellation options', url: '/cancellation' },
      { label: 'Report a concern', url: '/report' },
    ]
  };
  
  const defaultHosting = {
    links: [
      { label: 'Host an experience', url: '/host' },
      { label: 'Host resources', url: '/host-resources' },
      { label: 'Community forum', url: '/community' },
      { label: 'Food safety guidelines', url: '/food-safety' },
      { label: 'Insurance & liability', url: '/insurance' },
    ]
  };
  
  const defaultMealHoster = {
    links: [
      { label: 'How it works', url: '/how-it-works', direct: true },
      { label: 'Newsroom', url: '/newsroom' },
      { label: 'New experiences', url: '/new' },
      { label: 'Careers', url: '/careers' },
    ]
  };

  const defaultLegal = {
    links: [
      { label: 'Terms', url: '/terms' },
      { label: 'Sitemap', url: '/sitemap' },
      { label: 'Privacy', url: '/privacy', direct: true },
      { label: 'Your Privacy Choices', url: '/privacy-choices' },
    ]
  };

  const support = footerContent?.support || defaultSupport;
  const hosting = footerContent?.hosting || defaultHosting;
  const rawMealhoster = footerContent?.mealhoster || defaultMealHoster;
  // Always include "How it works" at the top of the MealHoster column
  const mealhoster = {
    ...rawMealhoster,
    links: [
      { label: 'How it works', url: '/how-it-works', direct: true },
      ...(rawMealhoster.links || []),
      { label: 'About Us', url: '/about', direct: true },
    ],
  };
  const rawLegal = footerContent?.legal || defaultLegal;
  // Ensure Privacy points to /privacy directly
  const legal = {
    ...rawLegal,
    links: (rawLegal.links || []).map(l =>
      l.url === '/privacy' ? { ...l, direct: true } : l
    ),
  };

  return (
    <footer className="bg-[#4A2E2A] text-white" data-testid="footer">
      <div className="max-w-[2000px] mx-auto px-6 md:px-10 lg:px-20 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Support */}
          <div>
            <h3 className="font-semibold mb-4 text-[#FF6B35]">{support.title || 'Support'}</h3>
            <ul className="space-y-3">
              {renderLinks(support, 'support')}
            </ul>
          </div>

          {/* Hosting */}
          <div>
            <h3 className="font-semibold mb-4 text-[#FF6B35]">{hosting.title || 'Hosting'}</h3>
            <ul className="space-y-3">
              {renderLinks(hosting, 'hosting')}
            </ul>
          </div>

          {/* MealHoster */}
          <div>
            <h3 className="font-semibold mb-4 text-[#FF6B35]">{mealhoster.title || 'MealHoster'}</h3>
            <ul className="space-y-3">
              {renderLinks(mealhoster, 'mealhoster')}
            </ul>
          </div>

          {/* Empty column for spacing on large screens */}
          <div className="hidden lg:block"></div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-[#5C3D38] mt-8 pt-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
              <span>&copy; 2026 MealHoster, Inc.</span>
              {legal.links?.map((link, index) => (
                <React.Fragment key={index}>
                  <span>&middot;</span>
                  <Link 
                    to={link.direct || ['/privacy', '/how-it-works'].includes(link.url) ? link.url : `/page/${link.url?.replace(/^\//, '')}`}
                    className="hover:text-white hover:underline"
                  >
                    {link.label}
                  </Link>
                </React.Fragment>
              ))}
            </div>

            <div className="flex items-center gap-6">
              <button className="flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-white hover:underline">
                <Globe className="w-4 h-4" />
                English (US)
              </button>
              <button className="text-sm font-medium text-gray-300 hover:text-white hover:underline">$ USD</button>
              <div className="flex items-center gap-4 text-gray-300">
                <a href="#" className="hover:text-white transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="#" className="hover:text-white transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="hover:text-white transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
